import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";

import { FOCUS_RING } from "../lib/focus";
import { HIT_AREA } from "../lib/target";
import { cn } from "../lib/utils";
import { useFieldControl } from "./field";
import { Icon } from "./icon";

/**
 * v2 Checkbox (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap.
 *
 * 16px box, radius 4, `--input` border; checked and indeterminate fill with
 * `--primary` and show a 12px Check / Minus. Their border is `--primary-accent`
 * (break #68): the same blue in light, a lighter edge in dark, so the box still
 * reads at 3.6:1 on a selected table row's `--primary-subtle` (the fill alone
 * is 2.5:1 there). Focus is Button's offset outline:
 * an inset outline would fill the box. The hit area is 24px through a pseudo
 * element, so the box does not grow in dense tables.
 *
 * Joins a Field through `useFieldControl`. For a label to the right use
 * `<Field orientation="inline">`; without visible text pass `aria-label`.
 *
 * The state is mirrored here and handed to Radix as controlled, so the classes
 * are plain conditionals (no `data-[state=…]` variants). `data-hover` /
 * `data-focus` show those states statically in stories, like Button.
 */

export type CheckedState = CheckboxPrimitive.CheckedState;

export type CheckboxProps = React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>;

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(function Checkbox(
  props,
  ref,
) {
  const { checked, defaultChecked, onCheckedChange, className, ...rest } = useFieldControl(props);
  const [uncontrolled, setUncontrolled] = React.useState<CheckedState>(defaultChecked ?? false);
  const controlled = checked !== undefined;
  const state = controlled ? checked : uncontrolled;
  const on = state !== false;
  const ariaInvalid = rest["aria-invalid"];
  const invalid = ariaInvalid !== undefined && ariaInvalid !== false && ariaInvalid !== "false";

  return (
    // Radix renders its hidden form input (inside a <form> only) as an absolute SIBLING after the
    // root, not inside it, so the root's own `relative` can't hold it. Without a positioned box
    // around both, the input's containing block is the page: it escapes a scrolling pane and
    // stretches the document (break #180).
    <span data-slot="checkbox-box" className="relative inline-flex shrink-0 align-middle">
      <CheckboxPrimitive.Root
        ref={ref}
        checked={state}
        onCheckedChange={(next) => {
          if (!controlled) setUncontrolled(next);
          onCheckedChange?.(next);
        }}
        className={cn(
          "relative inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-sm border transition-colors motion-reduce:transition-none",
          // 24px hit area around the 16px box.
          HIT_AREA,
          FOCUS_RING,
          invalid && "outline-destructive",
          "disabled:cursor-not-allowed disabled:opacity-50",
          on
            ? "border-primary-accent bg-primary text-primary-foreground enabled:hover:border-primary-hover enabled:hover:bg-primary-hover data-hover:border-primary-hover data-hover:bg-primary-hover"
            : invalid
              ? "border-destructive bg-transparent"
              : "border-input bg-transparent enabled:hover:border-ink-muted data-hover:border-ink-muted",
          className,
        )}
        {...rest}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center">
          <Icon icon={state === "indeterminate" ? Minus : Check} size={12} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    </span>
  );
});

export { Checkbox };
